/* Who can reach the ERP, and what they can reach.
 *
 * "Give Priya access to invoices" is the request. In ERPNext that is a role,
 * and a role's name tells you almost nothing: `Accounts User` opens 109
 * DocTypes across 13 modules and `Auditor` opens 19. So picking a role shows
 * what it actually reaches, read out of the permissions themselves.
 *
 * Taking access away never deletes anybody. Every document they touched
 * carries their name, and removing the user would either orphan a year of
 * paperwork or let the name be reused by somebody else.
 */
import { useCallback, useEffect, useState } from 'react'
import { useErp } from '@jackdesk/erp-kit'
import type { ErpError, Person, RoleReach } from '@jackdesk/erp-kit'
import { Failed, Loading, useToast } from '@jackdesk/shell'
import { labelFor } from './nav'

export function People() {
  const erp = useErp()
  const toast = useToast()
  const [people, setPeople] = useState<Person[] | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  const load = useCallback(() => {
    setFailed(null)
    erp.everyoneWithAccess().then((r) => setPeople(r.people)).catch((e) => setFailed(e as ErpError))
  }, [erp])
  useEffect(load, [load])

  if (failed) {
    return (
      <div className="page">
        <Failed message={failed.message} requestId={failed.requestId} onRetry={load} />
      </div>
    )
  }
  if (!people) return <div className="page"><Loading rows={5} label="Reading who is here" /></div>

  return (
    <div className="page" style={{ display: 'grid', gap: 16, maxWidth: 940 }}>
      <div>
        <h1>People and access</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '62ch' }}>
          Everyone who has opened the ERP for this organisation. Turning somebody off stops
          their access immediately and keeps everything they have ever done — their name
          stays on it.
        </p>
      </div>

      <div className="card">
        <table className="tbl">
          <thead>
            <tr><th>Who</th><th>What they can reach</th><th>Access</th><th></th></tr>
          </thead>
          <tbody>
            {people.map((p) => (
              <tr key={p.email} style={{ cursor: 'default' }}>
                <td>{p.email}</td>
                <td className="small muted">
                  {p.roles.length ? `${p.roles.length} role${p.roles.length === 1 ? '' : 's'}` : 'nothing yet'}
                </td>
                <td>
                  {p.disabled
                    ? <span className="chip chip-warn">turned off</span>
                    : <span className="chip chip-ok">on</span>}
                </td>
                <td>
                  <div className="row" style={{ gap: 6 }}>
                    <button className="btn btn-sm"
                            onClick={() => setOpen(open === p.email ? null : p.email)}>
                      {open === p.email ? 'Close' : 'Roles'}
                    </button>
                    <button className="btn btn-sm" onClick={async () => {
                      try {
                        await erp.setAccess(p.email, p.disabled)
                        toast('ok', p.disabled
                          ? `${p.email} can get in again.`
                          : `${p.email} cannot get in. Everything they did is still here.`)
                        load()
                      } catch (e) { toast('error', (e as ErpError).message) }
                    }}>
                      {p.disabled ? 'Let them back in' : 'Turn off'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open ? <TheirRoles email={open} onChanged={load} /> : null}
    </div>
  )
}

function TheirRoles({ email, onChanged }: { email: string; onChanged: () => void }) {
  const erp = useErp()
  const toast = useToast()
  const [held, setHeld] = useState<string[] | null>(null)
  const [offer, setOffer] = useState<{ name: string; ours: boolean }[]>([])
  const [looking, setLooking] = useState<string | null>(null)
  const [reach, setReach] = useState<RoleReach | null>(null)

  useEffect(() => {
    erp.personRoles(email).then((r) => setHeld(r.roles)).catch(() => setHeld([]))
    erp.rolesOnOffer().then((r) => setOffer(r.roles)).catch(() => setOffer([]))
  }, [erp, email])

  useEffect(() => {
    if (!looking) { setReach(null); return }
    let cancelled = false
    erp.whatARoleReaches(looking)
      .then((r) => { if (!cancelled) setReach(r) })
      .catch(() => { if (!cancelled) setReach(null) })
    return () => { cancelled = true }
  }, [erp, looking])

  if (!held) return <div className="card card-pad"><Loading rows={3} label="Reading their roles" /></div>

  const toggle = async (role: string) => {
    const next = held.includes(role) ? held.filter((r) => r !== role) : [...held, role]
    try {
      await erp.setPersonRoles(email, next)
      setHeld(next)
      toast('ok', held.includes(role) ? `${role} taken away.` : `${role} given.`)
      onChanged()
    } catch (e) { toast('error', (e as ErpError).message) }
  }

  return (
    <section className="card card-pad stack" style={{ gap: 12 }}>
      <div>
        <strong className="small">What {email} can do</strong>
        <p className="small muted" style={{ margin: '2px 0 0', maxWidth: '62ch' }}>
          A role's name does not say much. Click one to see what it actually opens before
          you give it to somebody.
        </p>
      </div>

      <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
        {offer.filter((r) => r.ours || held.includes(r.name)).map((r) => (
          <div key={r.name} className="row" style={{ gap: 8 }}>
            <label className="row" style={{ gap: 6, flex: 1 }}>
              <input type="checkbox" checked={held.includes(r.name)}
                     onChange={() => void toggle(r.name)} />
              <span className="small">{r.name}</span>
            </label>
            <button className="linkish small" aria-label={`What ${r.name} reaches`}
                    onClick={() => setLooking(looking === r.name ? null : r.name)}>
              {looking === r.name ? 'hide' : 'what?'}
            </button>
          </div>
        ))}
      </div>

      {looking ? (
        reach ? (
          <div className="card card-pad" style={{ background: 'var(--surface-2)' }}>
            <strong className="small">{reach.role} opens {reach.reaches} kinds of record</strong>
            <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
              {reach.modules.slice(0, 6).map((m) => (
                <div key={m.module}>
                  <div className="small" style={{ fontWeight: 560 }}>{m.module}</div>
                  {m.can_change.length ? (
                    <div className="small muted">
                      can change: {m.can_change.slice(0, 6).map(labelFor).join(', ')}
                      {m.can_change.length > 6 ? ` and ${m.can_change.length - 6} more` : ''}
                    </div>
                  ) : null}
                  {m.can_only_read.length ? (
                    <div className="small muted">
                      can only read: {m.can_only_read.slice(0, 5).map(labelFor).join(', ')}
                      {m.can_only_read.length > 5 ? ` and ${m.can_only_read.length - 5} more` : ''}
                    </div>
                  ) : null}
                </div>
              ))}
              {reach.modules.length > 6 ? (
                <div className="small muted">and {reach.modules.length - 6} more areas</div>
              ) : null}
            </div>
          </div>
        ) : <Loading rows={2} label={`Working out what ${looking} reaches`} />
      ) : null}
    </section>
  )
}
